import type { CSSProperties } from 'react';
import { AstralArt } from './AstralArt.js';

export interface CelestialHeroProps {
  /**
   * Which finished artwork this stage presents:
   *  - `home`  — moon.PNG   (the falling light is part of the asset)
   *  - `entry` — statue.PNG (the full statue including its pedestal base)
   */
  variant?: 'home' | 'entry';
  /** Makes the artwork the activation target (first entry only). */
  onActivate?: () => void;
  /** Accessible name for the activation control. */
  activateLabel?: string;
  /** True once activation has fired — plays the pulse and locks further taps. */
  activated?: boolean;
}

/**
 * Sparkle burst positions, in percent of the artwork box, spread around the
 * moon and upper pedestal so the burst reads as coming from several points
 * rather than one origin. Fixed values — no render-time randomness.
 */
const BURST = [
  { x: 50, y: 8, d: 0, dx: 0, dy: -20 },
  { x: 16, y: 22, d: 40, dx: -20, dy: -14 },
  { x: 84, y: 20, d: 30, dx: 20, dy: -16 },
  { x: 8, y: 42, d: 70, dx: -24, dy: -4 },
  { x: 92, y: 40, d: 60, dx: 24, dy: -6 },
  { x: 30, y: 54, d: 100, dx: -14, dy: 10 },
  { x: 70, y: 52, d: 90, dx: 14, dy: 8 },
] as const;

/**
 * The shared celestial stage. Home and first entry both render this, so the
 * surrounding atmosphere is identical by construction and only the centrepiece
 * and its halo behaviour differ.
 */
export function CelestialHero({
  variant = 'home',
  onActivate,
  activateLabel,
  activated = false,
}: CelestialHeroProps): JSX.Element {
  const interactive = typeof onActivate === 'function';

  const art = (
    <AstralArt
      name={variant === 'entry' ? 'statue' : 'moon'}
      halo={variant === 'entry' ? 'breathe' : 'flicker'}
      activated={activated}
      alt="Astral"
    />
  );

  return (
    <div className={`hero-moon hero-moon--${variant}`}>
      <div className="hero-art">
        {interactive ? (
          // Shrink-wraps the artwork, so the hit area is the art box and never
          // reaches the header or the language pill.
          <button
            type="button"
            className={`hero-art__hit${activated ? ' is-activated' : ''}`}
            onClick={onActivate}
            disabled={activated}
            aria-label={activateLabel}
          >
            {art}
            {activated && (
              <span className="hero-burst" aria-hidden="true">
                {BURST.map((b, i) => (
                  <span
                    key={i}
                    className="hero-burst__spark"
                    style={
                      {
                        left: `${b.x}%`,
                        top: `${b.y}%`,
                        '--bx': `${b.dx}px`,
                        '--by': `${b.dy}px`,
                        animationDelay: `${b.d}ms`,
                      } as CSSProperties
                    }
                  />
                ))}
              </span>
            )}
          </button>
        ) : (
          art
        )}
      </div>
    </div>
  );
}
